import { VendorViewModel, VendorSearchViewModel, PriceAgreementViewModel } from '@models/Vendor';
import { createReducer } from '@common/redux-utils';
import {
  VendorDashboardActionType,
  SearchVendorsInitAction,
  SearchVendorsFailureAction,
  SearchVendorsSuccessAction,
  GetTradesFailureAction,
  GetTradesSuccessAction,
  GetTradesStartAction,
  ClearVendorSearchAction,
  SearchPriceAgreementsInitAction,
  SearchPriceAgreementsFailureAction,
  SearchPriceAgreementsSuccessAction,
  ClearPriceAgreementsSearchAction
} from './vendor-dashboard.actions';
import { TradeOptionsViewModel } from '@models/ServiceWorkOrder';
import { OptionsViewModel } from '@models/common.models';

export interface VendorDashboardState {
  readonly message?: string;
  readonly vendorVM: VendorViewModel;
  readonly id: number;

  readonly searchQuery: string | null;
  readonly searchLoading: boolean;
  readonly searchPageCount: number;
  readonly searchPageNumber: number;
  readonly searchPageSize: number;
  readonly searchResults: VendorSearchViewModel[];
  readonly searchResultsTotalCount: number;

  readonly trade?: TradeOptionsViewModel;
  readonly tradeOptions?: TradeOptionsViewModel[];

  readonly type?: OptionsViewModel;
  readonly status?: OptionsViewModel;

  readonly cviFilter?: OptionsViewModel;
  readonly paymentType?: string;
  readonly priceAgreementsSearchQuery: string | null;
  readonly priceAgreementsSearchLoading: boolean;
  readonly priceAgreementsSearchPageCount: number;
  readonly priceAgreementsSearchPageNumber: number;
  readonly priceAgreementsSearchPageSize: number;
  readonly priceAgreementsSearchResults: PriceAgreementViewModel[];
  readonly priceAgreementsSearchResultsTotalCount: number;
  readonly group?: OptionsViewModel;
}

const initialState: VendorDashboardState = {
  vendorVM: {} as VendorViewModel,
  id: 0,
  searchQuery: null,
  searchLoading: false,
  searchPageCount: 0,
  searchPageNumber: 1,
  searchPageSize: 10,
  searchResults: [],
  searchResultsTotalCount: 0,
  priceAgreementsSearchQuery: null,
  priceAgreementsSearchLoading: false,
  priceAgreementsSearchPageCount: 0,
  priceAgreementsSearchPageNumber: 1,
  priceAgreementsSearchPageSize: 10,
  priceAgreementsSearchResults: [],
  priceAgreementsSearchResultsTotalCount: 0
};

/***** Get Trades ****/
function handleGetTradesFailure(state: VendorDashboardState, action: GetTradesFailureAction): VendorDashboardState {
  return {
    ...state
  };
}

function handleGetTradesStart(state: VendorDashboardState, action: GetTradesStartAction): VendorDashboardState {
  return {
    ...state
  };
}

function handleGetTradesSuccess(state: VendorDashboardState, action: GetTradesSuccessAction): VendorDashboardState {
  return {
    ...state,
    tradeOptions: action.results
  };
}

/********** Search **********/
function handleSearchVendorsInit(state: VendorDashboardState, action: SearchVendorsInitAction): VendorDashboardState {
  return {
    ...state,
    message: 'Search Init!',
    searchQuery: action.query,
    type: action.vendorType,
    trade: action.vendorTrade,
    status: action.vendorStatus,
    searchLoading: true
  };
}
function handleSearchVendorsFailure(
  state: VendorDashboardState,
  action: SearchVendorsFailureAction
): VendorDashboardState {
  return {
    ...state,
    message: action.errorMessage,
    searchLoading: false
  };
}
function handleSearchVendorsSuccess(
  state: VendorDashboardState,
  action: SearchVendorsSuccessAction
): VendorDashboardState {
  return {
    ...state,
    message: 'Search Success!',
    searchLoading: false,
    searchPageCount: Math.ceil(action.searchResult.totalResults / action.searchResult.pageSize),
    searchPageNumber: action.pageNumber,
    searchPageSize: action.pageSize,
    searchResults: action.searchResult.results,
    searchResultsTotalCount: action.searchResult.totalResults
  };
}

function handleClearVendorSearch(state: VendorDashboardState, action: ClearVendorSearchAction): VendorDashboardState {
  return {
    ...state,
    searchResults: initialState.searchResults,
    searchPageCount: initialState.searchPageCount,
    searchPageSize: initialState.searchPageSize,
    searchPageNumber: initialState.searchPageNumber
  };
}

function handleSearchPriceAgreementsInit(
  state: VendorDashboardState,
  action: SearchPriceAgreementsInitAction
): VendorDashboardState {
  return {
    ...state,
    message: 'Search Init!',
    searchQuery: action.query,
    paymentType: action.paymentType,
    cviFilter: action.cviFilter,
    searchLoading: true
  };
}
function handleSearchPriceAgreementsFailure(
  state: VendorDashboardState,
  action: SearchPriceAgreementsFailureAction
): VendorDashboardState {
  return {
    ...state,
    message: action.errorMessage,
    searchLoading: false
  };
}
function handleSearchPriceAgreementsSuccess(
  state: VendorDashboardState,
  action: SearchPriceAgreementsSuccessAction
): VendorDashboardState {
  return {
    ...state,
    message: 'Search Success!',
    priceAgreementsSearchLoading: false,
    priceAgreementsSearchPageCount: Math.ceil(action.searchResult.totalResults / action.searchResult.pageSize),
    priceAgreementsSearchPageNumber: action.pageNumber,
    priceAgreementsSearchPageSize: action.pageSize,
    priceAgreementsSearchResults: action.searchResult.results,
    priceAgreementsSearchResultsTotalCount: action.searchResult.totalResults
  };
}

function handleClearPriceAgreementsSearch(
  state: VendorDashboardState,
  action: ClearPriceAgreementsSearchAction
): VendorDashboardState {
  return {
    ...state,
    priceAgreementsSearchResults: initialState.priceAgreementsSearchResults,
    priceAgreementsSearchPageCount: initialState.priceAgreementsSearchPageCount,
    priceAgreementsSearchPageSize: initialState.priceAgreementsSearchPageSize,
    priceAgreementsSearchPageNumber: initialState.priceAgreementsSearchPageNumber
  };
}

export const VendorMaintenanceReducer = createReducer(initialState, {
  /***** Get Trades ****/
  [VendorDashboardActionType.GetTradesStart]: handleGetTradesStart,
  [VendorDashboardActionType.GetTradesSuccess]: handleGetTradesSuccess,
  [VendorDashboardActionType.GetTradesFailure]: handleGetTradesFailure,
  /********** Search **********/
  [VendorDashboardActionType.SearchVendorsInit]: handleSearchVendorsInit,
  [VendorDashboardActionType.SearchVendorsFailure]: handleSearchVendorsFailure,
  [VendorDashboardActionType.SearchVendorsSuccess]: handleSearchVendorsSuccess,
  [VendorDashboardActionType.ClearVendorSearch]: handleClearVendorSearch,

  [VendorDashboardActionType.SearchPriceAgreementsInit]: handleSearchPriceAgreementsInit,
  [VendorDashboardActionType.SearchPriceAgreementsFailure]: handleSearchPriceAgreementsFailure,
  [VendorDashboardActionType.SearchPriceAgreementsSuccess]: handleSearchPriceAgreementsSuccess,
  [VendorDashboardActionType.ClearPriceAgreementsSearch]: handleClearPriceAgreementsSearch
});



// WEBPACK FOOTER //
// ./src/vendor/_components/vendor-dashboard/vendor-dashboard.reducer.ts